import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/stores/auth';
import { useCrypto } from '../lib/stores/crypto';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { restoreKey } = useCrypto();

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [quickCaptureCount] = useState(3);

  useEffect(() => {
    // Restore encryption key from session storage on mount
    restoreKey();
  }, []);

  useEffect(() => {
    // Check device type
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const handleNavigation = (path: string) => {
    if (path.startsWith('/')) {
      navigate(path);
    } else {
      // Placeholder for unavailable features
      alert(`${path} - Coming soon!`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/auth');
  };

  const handleQuickCapture = () => {
    alert('Quick Capture - Coming soon!');
  };

  const isActive = (path: string) => {
    if (path === '/' || path === '/home') {
      return location.pathname === '/' || location.pathname === '/home';
    }
    return location.pathname.startsWith(path);
  };

  const userName = user?.name?.split(' ')[0] || 'there';
  const userInitial = (user?.name?.[0] || user?.email?.[0] || 'U').toUpperCase();

  return (
    <div className="app-layout">
      {/* Desktop/Tablet Sidebar */}
      {!isMobile && (
        <aside className={`app-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sb-header">
            <div className="sb-logo">
              <span className="lock">◈</span>
              {!sidebarCollapsed && <span>MySecureRoom</span>}
            </div>
            {isTablet && (
              <button
                className="sb-toggle"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? '›' : '‹'}
              </button>
            )}
          </div>

          <button className="sb-capture" onClick={handleQuickCapture} title="Quick Capture (⌘K)">
            {sidebarCollapsed ? '+' : (
              <>+ Capture <span className="kbd">⌘K</span></>
            )}
          </button>

          <div className={`sb-item ${isActive('/home') || isActive('/') ? 'active' : ''}`} onClick={() => handleNavigation('/home')} title="Today">
            <span className="icon">◉</span>
            {!sidebarCollapsed && <span>Today</span>}
          </div>

          {!sidebarCollapsed && <div className="sb-section">Plan</div>}
          <div className={`sb-item ${isActive('/purpose/foundation') || isActive('/purpose/ikigai') || isActive('/purpose/values') || isActive('/purpose/wheel') || isActive('/purpose/vision') ? 'active' : ''}`} onClick={() => handleNavigation('/purpose/foundation')} title="Foundation">
            <span className="icon">🧭</span>
            {!sidebarCollapsed && <span>Foundation</span>}
          </div>
          <div className={`sb-item ${isActive('/purpose/goals') ? 'active' : ''}`} onClick={() => handleNavigation('/purpose/goals')} title="Goals">
            <span className="icon">🎯</span>
            {!sidebarCollapsed && <span>Goals</span>}
          </div>
          <div className={`sb-item ${isActive('/tools/decisions') ? 'active' : ''}`} onClick={() => handleNavigation('/tools/decisions')} title="Decide">
            <span className="icon">⚖️</span>
            {!sidebarCollapsed && <span>Decide</span>}
          </div>

          {!sidebarCollapsed && <div className="sb-section">Execute</div>}
          <div className="sb-item" onClick={() => handleNavigation('Projects')} title="Projects">
            <span className="icon">📋</span>
            {!sidebarCollapsed && <span>Projects</span>}
          </div>
          <div className="sb-item" onClick={() => handleNavigation('Weekly Planner')} title="Weekly Planner">
            <span className="icon">📅</span>
            {!sidebarCollapsed && <span>Weekly Planner</span>}
          </div>
          <div className="sb-item" onClick={() => handleNavigation('Habits & Reminders')} title="Habits & Reminders">
            <span className="icon">⚡</span>
            {!sidebarCollapsed && <span>Habits & Reminders</span>}
          </div>
          <div className="sb-item" onClick={handleQuickCapture} title="Quick Capture">
            <span className="icon">💭</span>
            {!sidebarCollapsed && <span>Quick Capture</span>}
            {quickCaptureCount > 0 && !sidebarCollapsed && <span className="badge">{quickCaptureCount}</span>}
          </div>

          {!sidebarCollapsed && <div className="sb-section">Reflect</div>}
          <div className="sb-item" onClick={() => handleNavigation('Journal')} title="Journal">
            <span className="icon">📓</span>
            {!sidebarCollapsed && <span>Journal</span>}
          </div>
          <div className="sb-item" onClick={() => handleNavigation('Checkpoints')} title="Checkpoints">
            <span className="icon">📊</span>
            {!sidebarCollapsed && <span>Checkpoints</span>}
          </div>
          <div className="sb-item" onClick={() => handleNavigation('Insights')} title="Insights">
            <span className="icon">💡</span>
            {!sidebarCollapsed && <span>Insights</span>}
          </div>

          {!sidebarCollapsed && <div className="sb-section">Life</div>}
          <div className="sb-item" onClick={() => handleNavigation('Finance')} title="Finance">
            <span className="icon">💰</span>
            {!sidebarCollapsed && <span>Finance</span>}
          </div>
          <div className="sb-item" onClick={() => handleNavigation('Health')} title="Health">
            <span className="icon">❤️</span>
            {!sidebarCollapsed && <span>Health</span>}
          </div>
          <div className="sb-item" onClick={() => handleNavigation('Learning')} title="Learning">
            <span className="icon">📚</span>
            {!sidebarCollapsed && <span>Learning</span>}
          </div>
          <div className="sb-item" onClick={() => handleNavigation('People')} title="People">
            <span className="icon">👥</span>
            {!sidebarCollapsed && <span>People</span>}
          </div>

          <div className="sb-bottom">
            <div className="sb-item" onClick={handleLogout} title="Settings">
              <span className="icon">⚙️</span>
              {!sidebarCollapsed && <span>Settings</span>}
            </div>
            {!sidebarCollapsed && (
              <div className="sb-upgrade">
                Upgrade to Pro
                <span>Unlock AI Coach & unlimited features</span>
              </div>
            )}
          </div>
        </aside>
      )}

      {/* Mobile Top Bar */}
      {isMobile && (
        <div className="app-topbar">
          <div className="topbar-logo">
            <span className="lock">◈</span> MySecureRoom
          </div>
          <div className="topbar-avatar" onClick={handleLogout}>
            {userInitial}
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="app-main">
        {children}
      </main>

      {/* Mobile Bottom Tabs */}
      {isMobile && (
        <div className="app-tabs">
          <div className="tab" onClick={() => handleNavigation('Plan')}>
            <span className="tab-icon">📐</span>
            Plan
          </div>
          <div className={`tab ${isActive('/home') || isActive('/') ? 'active' : ''}`} onClick={() => handleNavigation('/home')}>
            <span className="tab-icon">◉</span>
            Today
          </div>
          <div className="tab" onClick={() => handleNavigation('Execute')}>
            <span className="tab-icon">⚡</span>
            Execute
          </div>
          <div className="tab" onClick={() => handleNavigation('Reflect')}>
            <span className="tab-icon">◑</span>
            Reflect
          </div>
        </div>
      )}
    </div>
  );
}
